// @ts-nocheck
import React, { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ChevronDown, Upload, FileText, Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const BUCKET = "compliance-docs";

const CREDENTIAL_TYPES = [
  { value: "background_check", label: "Background check", gating: true },
  { value: "safesport", label: "SafeSport", gating: true },
  { value: "id_verification", label: "ID verification", gating: false },
  { value: "age_verification", label: "Age verification", gating: false },
];

const STATUSES = ["none", "pending", "cleared", "expired", "failed"];

const statusClass = (status: string) =>
  status === "cleared"
    ? "bg-emerald-100 text-emerald-900 hover:bg-emerald-100"
    : status === "pending"
      ? "bg-amber-100 text-amber-900 hover:bg-amber-100"
      : status === "none"
        ? "bg-muted text-muted-foreground hover:bg-muted"
        : "bg-rose-100 text-rose-900 hover:bg-rose-100";

function ViewDocumentLink({ path }: { path: string }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    const { data, error } = await supabase.storage.from(BUCKET).createSignedUrl(path, 3600);
    setBusy(false);
    if (error || !data?.signedUrl) {
      toast.error(error?.message || "Could not open document");
      return;
    }
    window.open(data.signedUrl, "_blank", "noopener,noreferrer");
  };
  return (
    <Button type="button" variant="link" size="sm" className="h-auto p-0 text-xs" onClick={open} disabled={busy}>
      {busy ? <Loader2 className="mr-1 h-3 w-3 animate-spin" /> : <FileText className="mr-1 h-3 w-3" />}
      View document
    </Button>
  );
}

function CredentialRow({
  type,
  cred,
  orgId,
  officialId,
  onSave,
}: {
  type: (typeof CREDENTIAL_TYPES)[number];
  cred: any;
  orgId: string | null;
  officialId: string | null;
  onSave: (credentialType: string, values: any) => Promise<void>;
}) {
  const [openForm, setOpenForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const status = cred?.status || "none";

  const initial = () => ({
    status: cred?.status || "none",
    provider: cred?.provider || "",
    external_ref: cred?.external_ref || "",
    document_url: cred?.document_url || "",
    document_name: "",
    expires_at: cred?.expires_at ? String(cred.expires_at).slice(0, 10) : "",
    notes: cred?.notes || "",
    review_reason: cred?.review_reason || "",
  });

  const [form, setForm] = useState(initial);
  useEffect(() => { setForm(initial()); }, [cred]);

  const handleFile = async (file?: File) => {
    if (!file || !orgId || !officialId) return;
    setUploading(true);
    const path = `${orgId}/${officialId}/${type.value}-${Date.now()}-${file.name}`;
    const { error } = await supabase.storage.from(BUCKET).upload(path, file, { upsert: false });
    setUploading(false);
    if (error) {
      toast.error(error.message);
      return;
    }
    setForm((f) => ({ ...f, document_url: path, document_name: file.name }));
    toast.success("Document uploaded — save to attach it");
  };

  const submit = async () => {
    if (form.status === "failed" && !form.review_reason.trim()) {
      toast.error("A reason for decline is required");
      return;
    }
    setSaving(true);
    try {
      await onSave(type.value, form);
      setOpenForm(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={cn("rounded-md border bg-background p-3", type.gating && "border-primary/40 bg-primary/5")}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-sm font-medium">{type.label}</p>
          <p className="text-xs text-muted-foreground">
            {cred?.provider || "No provider"}
            {cred?.expires_at ? ` · expires ${new Date(cred.expires_at).toLocaleDateString()}` : ""}
          </p>
          {cred?.document_url && (
            <div className="mt-1"><ViewDocumentLink path={cred.document_url} /></div>
          )}
          {cred?.notes && (
            <p className="mt-1 text-xs text-muted-foreground">
              <span className="font-medium">Notes:</span> {cred.notes}
            </p>
          )}
          {status === "failed" && cred?.review_reason && (
            <p className="mt-1 text-xs text-rose-700">
              <span className="font-medium">Reason for decline:</span> {cred.review_reason}
            </p>
          )}
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Badge variant="secondary" className={statusClass(status)}>{status}</Badge>
          <Button size="sm" variant="ghost" onClick={() => setOpenForm((v) => !v)}>
            Update <ChevronDown className={cn("ml-1 h-3.5 w-3.5 transition", openForm && "rotate-180")} />
          </Button>
        </div>
      </div>

      {openForm && (
        <div className="mt-3 space-y-3 border-t pt-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-xs">Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Expires</Label>
              <Input type="date" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Provider</Label>
              <Input value={form.provider} onChange={(e) => setForm({ ...form, provider: e.target.value })} placeholder="e.g. NCSI" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">External reference</Label>
              <Input
                value={form.external_ref}
                onChange={(e) => setForm({ ...form, external_ref: e.target.value })}
                placeholder="NSID / case number"
              />
            </div>
          </div>

          {form.status === "failed" && (
            <div className="space-y-1">
              <Label className="text-xs text-rose-700">Reason for decline *</Label>
              <Input
                value={form.review_reason}
                onChange={(e) => setForm({ ...form, review_reason: e.target.value })}
                placeholder="Why was this credential declined?"
              />
            </div>
          )}

          <div className="space-y-1">
            <Label className="text-xs">Document</Label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                ref={fileRef}
                type="file"
                className="hidden"
                onChange={(e) => handleFile(e.target.files?.[0])}
              />
              <Button type="button" variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading}>
                {uploading ? <Loader2 className="mr-1 h-3.5 w-3.5 animate-spin" /> : <Upload className="mr-1 h-3.5 w-3.5" />}
                {uploading ? "Uploading…" : "Upload document"}
              </Button>
              {form.document_url && (
                <>
                  <span className="text-xs text-muted-foreground truncate max-w-[220px]">
                    {form.document_name || form.document_url.split("/").pop()}
                  </span>
                  <ViewDocumentLink path={form.document_url} />
                </>
              )}
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs">Notes (optional)</Label>
            <Textarea
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Internal notes"
            />
          </div>

          <Button size="sm" onClick={submit} disabled={saving || uploading} className="w-full">
            {saving ? "Saving…" : `Save ${type.label.toLowerCase()}`}
          </Button>
        </div>
      )}
    </div>
  );
}

interface Props {
  officialId: string | null;
  orgId: string | null;
  officialName?: string;
  onChanged?: () => void;
}

export default function OfficialCompliancePanel({ officialId, orgId, officialName, onChanged }: Props) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<any>(null);

  const load = async () => {
    if (!officialId) return;
    setLoading(true);
    const { data: res, error } = await supabase.rpc("get_official_compliance", { p_official_id: officialId });
    if (error) toast.error(error.message);
    setData(res || null);
    setLoading(false);
  };

  useEffect(() => { load(); }, [officialId]);

  const byType = useMemo(() => {
    const map: Record<string, any> = {};
    for (const c of data?.credentials ?? []) map[c.credential_type] = c;
    return map;
  }, [data]);

  const saveCredential = async (credentialType: string, values: any) => {
    if (!orgId || !officialId) return;
    const { error } = await supabase.rpc("set_compliance_credential", {
      p_org_id: orgId,
      p_subject_type: "official",
      p_subject_ref_id: officialId,
      p_credential_type: credentialType,
      p_status: values.status,
      p_provider: values.provider || null,
      p_external_ref: values.external_ref || null,
      p_document_url: values.document_url || null,
      p_expires_at: values.expires_at ? new Date(values.expires_at).toISOString() : null,
      p_notes: values.notes || null,
      p_review_reason: values.status === "failed" ? values.review_reason || null : null,
    });
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success("Credential updated");
    await load();
    onChanged?.();
  };

  const cleared = !!data?.is_cleared;

  return (
    <div className="space-y-4 rounded-lg border bg-muted/30 p-4">
      <p className="text-sm font-semibold">Compliance — {officialName || "Official"}</p>
      {loading ? (
        <Skeleton className="h-24 w-full" />
      ) : (
        <>
          <div
            className={cn(
              "flex items-center gap-3 rounded-lg border p-3",
              cleared
                ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                : "border-rose-300 bg-rose-50 text-rose-900"
            )}
          >
            {cleared ? <ShieldCheck className="h-6 w-6" /> : <ShieldAlert className="h-6 w-6" />}
            <span className="text-base font-bold">{cleared ? "Cleared to officiate" : "Not cleared"}</span>
          </div>

          <div className="space-y-3">
            {CREDENTIAL_TYPES.map((t) => (
              <React.Fragment key={t.value}>
                <CredentialRow
                  type={t}
                  cred={byType[t.value]}
                  orgId={orgId}
                  officialId={officialId}
                  onSave={saveCredential}
                />
                {t.value === "safesport" && (
                  <p className="px-1 text-xs text-muted-foreground">
                    Both must be Cleared for this official to be assignable to games.
                  </p>
                )}
              </React.Fragment>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
